/**
 * Pure formatting helpers for book export.
 *
 * Kept out of bookExport.js so they can be tested without pulling in jsPDF,
 * whose Node build re-exports untranspiled TypeScript that the test runner
 * cannot load.
 */

/**
 * Escape text for XML/XHTML.
 *
 * EPUB is strict XHTML — a reader refuses to open a file containing a bare "&".
 * Titles and memoir prose contain them routinely ("Mum & Dad", "Smith & Sons"),
 * and nothing in the EPUB path escaped anything, so a single ampersand anywhere
 * in the book produced a file that would not open at all.
 */
export function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Roman numerals for any chapter count.
 * The lookup table this replaces was a 12-element array, so a book's thirteenth
 * chapter silently fell back to "13" in the middle of a roman-numbered book.
 */
export function toRoman(value) {
  const numerals = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let remaining = Math.max(1, Math.floor(Number(value) || 1));
  let out = '';
  for (const [amount, symbol] of numerals) {
    while (remaining >= amount) {
      out += symbol;
      remaining -= amount;
    }
  }
  return out;
}

/**
 * Render a chapter label in the style the AI format guide asked for.
 * The shipped exporter always printed "Chapter N", so every style the guide
 * could choose had no effect on the finished book.
 */
export function formatChapterLabel(number, style) {
  switch (style) {
    case 'simple':
      return `${number}`;
    case 'roman':
      return toRoman(number);
    case 'bold':
      return `CHAPTER ${number}`;
    case 'playful':
      return `~ ${number} ~`;
    case 'elegant':
    default:
      return `Chapter ${number}`;
  }
}

/**
 * Split a paragraph for a drop cap: the first `capLines` lines sit beside the
 * capital, the rest run full width.
 *
 * `measure` wraps text to a width and returns lines (jsPDF's splitTextToSize).
 *
 * Splits on word count rather than character offset, because the wrapper
 * normalises whitespace — slicing the source string by the wrapped length drops
 * or duplicates characters mid-sentence.
 */
export function splitForDropCap(paragraph, capLines, measure, narrowWidth, fullWidth) {
  const rest = String(paragraph ?? '').slice(1);
  if (!rest.trim()) return { lines: [], capLineCount: 0 };

  const narrowLines = measure(rest, narrowWidth);
  const capLineCount = Math.max(0, Math.min(capLines, narrowLines.length));

  const consumed = narrowLines
    .slice(0, capLineCount)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;

  const remainingWords = rest.split(/\s+/).filter(Boolean).slice(consumed).join(' ');

  return {
    lines: narrowLines.slice(0, capLineCount).concat(
      remainingWords ? measure(remainingWords, fullWidth) : []
    ),
    capLineCount,
  };
}
